import { useState, useRef, useEffect } from "react";
import { Place, EVENT_TYPES } from "shared/topchef.types";
import { useTranslation } from "react-i18next";

export interface MyAccountFirstConnectionProps {
  loggedInPlace: Place,
  updateAcount: (place: Place) => void;
}

const MyAccountFirstConnection = (props: MyAccountFirstConnectionProps) => {
  const { t } = useTranslation();
  const lochallTermsUrl = String(t('externalUrls.lochall.terms'));
  const lochallCharterUrl = String(t('externalUrls.lochall.charter'));
  
  interface MyAccountForm {
    maxGuests: number;
    budget: number;
    acceptReward: boolean;
    supportedEventTypes: string[]
  }

  const yesRadioRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<MyAccountForm>({
    maxGuests: props.loggedInPlace.maxGuests,
    budget: props.loggedInPlace.budget,
    acceptReward: props.loggedInPlace.acceptReward,
    supportedEventTypes: props.loggedInPlace.supportedEventTypes
  });

  // Simule un clic sur "Oui" au montage
  useEffect(() => {
    if (yesRadioRef.current) {
      yesRadioRef.current.click();
    }
  }, []);

  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleTermsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTermsAccepted(event.target.checked);
  };

  const handleInputEventChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    
    let value: string | boolean = event.target.value;
    if (name === "acceptReward") {
      value = JSON.parse(value.toLowerCase());
    }

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };


  const handleCheckboxChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      supportedEventTypes: prev.supportedEventTypes.includes(value)
        ? prev.supportedEventTypes.filter((item) => item !== value)
        : [...prev.supportedEventTypes, value],
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const placeToUpdate: Place = {
      id: props.loggedInPlace.id,
      name: props.loggedInPlace.name,
      wpId: props.loggedInPlace.wpId,
      firstConnection: false,
      city: props.loggedInPlace.city,
      region: props.loggedInPlace.region,
      maxGuests: Number(formData.maxGuests),
      budget: formData.budget,
      acceptReward: formData.acceptReward,
      supportedEventTypes: formData.supportedEventTypes,
      locHallPoints: 0
    };

   props.updateAcount(placeToUpdate);

  };

  return (
      <div className="page-final-profil-settings page-profil container p-5 overflow-auto">

        <div id="welcome_message" className="card rounded p-3 mb-4 border-success">
          <h5 className="m-0 text-success">{t('account.welcome')}</h5>
        </div>
        <h5 className="mb-3">{t('account.finalizeProfile')}</h5>


          <form className="card shadow-sm p-4 align-items-center" onSubmit={handleSubmit}>                        
              <div id="commission_acceptance" className="mb-4 text-center">
                  <p className="fw-semibold mb-2">{t('account.acceptsCommissions')}</p>
                  
                  <div className="d-flex gap-3 justify-content-center">
                      <input type="radio" id="accept_commissions" name="acceptReward" value="true" ref={yesRadioRef} className="btn-check" required defaultChecked={formData.acceptReward} onChange={handleInputEventChange} />
                        <label className="btn btn-outline-success" htmlFor="accept_commissions">{t('account.yes')}</label>
                      <input type="radio" id="refuse_commissions" name="acceptReward" value="false" className="btn-check" required defaultChecked={formData.acceptReward === false} onChange={handleInputEventChange} />
                        <label className="btn btn-outline-danger" htmlFor="refuse_commissions">{t('account.no')}</label>
                  </div>
              </div>
      
              <div id="event_types" className="mb-4">
                      <p className="fw-semibold text-center">{t('account.eventTypesQuestion')} <i>{t('account.multipleSelection')}</i></p>
                  <div id="event_types_list" className="d-flex flex-wrap gap-2">
                    {EVENT_TYPES.map((eventType) => (
                      <div key={eventType.value}>
                        <input type="checkbox" className="btn-check" id={eventType.value} value={eventType.value} autoComplete="off" name="supportedEventTypes" checked={formData.supportedEventTypes.includes(eventType.value)} onChange={() => handleCheckboxChange(eventType.value)}/>
                        <label className="btn btn-outline-primary" htmlFor={eventType.value}>{t(eventType.label)}</label>
                      </div>
                    ))}
                  </div>
              </div>
          
              <div id="budget_container" className="mb-3 col-md-6 text-center justify-content-center">
                  <label htmlFor="budget" className="form-label fw-semibold">{t('account.minBudget')}</label>
                  <input type="number" id="budget" name="budget" className="form-control w-20 m-auto" placeholder={String(t('leadEditor.amountExVat'))} required min="0" value={formData.budget} onChange={handleInputEventChange} />
              </div>

              <div id="invites_container" className="mb-3 col-md-6 text-center justify-content-center">
                  <label htmlFor="maxGuests" className="form-label fw-semibold">{t('account.maxGuests')}</label>
                  <input type="number" id="maxGuests" name="maxGuests" className="form-control w-20 m-auto" placeholder={String(t('account.maxCapacity'))} required min="0" value={formData.maxGuests} onChange={handleInputEventChange} />
              </div>

              <div id="accept_terms_of_service_container" className="mb-3 col-md-8 text-center justify-content-center" data-testid="accept_terms_of_service">
                <input type="checkbox" id="terms_of_use" className="m-2 mb-0 mt-0" autoComplete="off" required name="accept_terms_of_use" checked={termsAccepted} onChange={handleTermsChange} />
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid*/}
                <label htmlFor="terms_of_use" className="form-label">
                  {t('account.acceptTermsPrefix')}
                  {' '}
                  <a href={lochallTermsUrl} target="_blank" rel="noreferrer">{t('account.termsOfReference')}</a>
                  {' '}
                  {t('account.and')}
                  {' '}
                  <a href={lochallCharterUrl} target="_blank" rel="noreferrer">{t('account.conductCharter')}</a>
                  {' '}
                  {t('account.ofLinkEvents')}
                </label>
              </div>

              <div className="mt-2 text-center btn-list">
                  <button id="save_profil_settings" className="btn btn-primary" disabled={(termsAccepted===false)}>{t('common.save')}</button>
              </div>
          </form>
          
      </div>
  );

}

export default MyAccountFirstConnection;
